/**
 * React Query hooks for channel annotations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getChatsWithAnnotations,
  updateChannelAnnotation,
  deleteChannelAnnotation,
  getChatFileStats,
} from '@/api/channelAnnotations';
import { ChatWithAnnotation, ChannelAnnotation, ChannelAnnotationUpdate, ChatFileStatsResponse } from '@/types/api';
import { getErrorMessage } from '@/api/client';

export const useChatsWithAnnotations = (manuscriptOnly: boolean = false) => {
  return useQuery<ChatWithAnnotation[], Error>({
    queryKey: ['chats-with-annotations', manuscriptOnly],
    queryFn: () => getChatsWithAnnotations(manuscriptOnly),
  });
};

export const useUpdateAnnotation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ChannelAnnotation,
    Error,
    { chatId: number; data: ChannelAnnotationUpdate }
  >({
    mutationFn: ({ chatId, data }) => updateChannelAnnotation(chatId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats-with-annotations'] });
    },
    onError: (error) => {
      console.error('Failed to update annotation:', getErrorMessage(error));
    },
  });
};

export const useDeleteAnnotation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: deleteChannelAnnotation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chats-with-annotations'] });
    },
    onError: (error) => {
      console.error('Failed to delete annotation:', getErrorMessage(error));
    },
  });
};

export const useChatFileStats = (chatId: number | null) => {
  return useQuery<ChatFileStatsResponse, Error>({
    queryKey: ['chat-file-stats', chatId],
    queryFn: () => getChatFileStats(chatId!),
    enabled: chatId !== null,
    staleTime: 5 * 60 * 1000,
  });
};
